import dayjs from 'dayjs'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { orderApi } from '@/core/api/order'
import { useSelectNetwork } from '@/core/services/network'
import { useAcceptConsultationOrderMutation, useGetConsultationOrdersQuery } from '@/core/services/order'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { getApiErrorMessage } from '@/utils/errors'
import { isDoctor } from '@/utils/type-guards'

import ConsultationOrders from '../components/ConsultationOrders'

export default function ConsultationOrdersContainer() {
  const dispatch = useAppDispatch()
  const { networkId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const authUser = useAppSelector(selectAuthUser)

  const { data: network } = useSelectNetwork(networkId)

  const { data: rawConsultationOrders, originalArgs: getConsultationOrdersArg } = useGetConsultationOrdersQuery(
    { network: networkId },
    { refetchOnMountOrArgChange: true, skip: !networkId }
  )
  const [acceptConsultationOrder, { isLoading: isAcceptingConsultationOrder, originalArgs: acceptedConsultationOrderId }] = useAcceptConsultationOrderMutation()

  const isNetworkMember = useMemo(() => !!network?.members.find((member) => member === authUser?._id), [authUser, network])

  const consultationOrders = useMemo(
    () =>
      rawConsultationOrders?.map((consultationOrder) => ({
        id: consultationOrder._id,
        acceptedBy: consultationOrder.acceptedBy ? `${consultationOrder.acceptedBy.userInfo.name} ${consultationOrder.acceptedBy.userInfo.surname}` : '',
        canAccept: isDoctor(authUser) && isNetworkMember,
        createdBy: `${consultationOrder.createdBy.userInfo.name} ${consultationOrder.createdBy.userInfo.surname}`,
        consultationId: consultationOrder.consultation._id,
        isAccepting: isAcceptingConsultationOrder && acceptedConsultationOrderId === consultationOrder._id,
        patient: `${consultationOrder.patient.userInfo.name} ${consultationOrder.patient.userInfo.surname}`,
        speciality: '',
        status: consultationOrder.status,
        startDate: dayjs(consultationOrder.startDate).format('DD/MM/YYYY HH:mm'),
      })) ?? [],
    [acceptedConsultationOrderId, authUser, isAcceptingConsultationOrder, isNetworkMember, rawConsultationOrders]
  )

  return (
    <ConsultationOrders
      consultationOrders={consultationOrders}
      onAccept={async (consultationOrderId) => {
        if (!authUser) return
        try {
          const acceptedConsultationOrder = await acceptConsultationOrder(consultationOrderId).unwrap()
          // manual update cache
          if (getConsultationOrdersArg) {
            dispatch(
              orderApi.util.updateQueryData('getConsultationOrders', getConsultationOrdersArg, (draftConsultationOrders) => {
                for (const draftConsultationOrder of draftConsultationOrders) {
                  if (draftConsultationOrder._id !== consultationOrderId) continue
                  Object.assign(draftConsultationOrder, acceptedConsultationOrder)
                }
              })
            )
          }
        } catch (error) {
          enqueueSnackbar(getApiErrorMessage(error, t('notification.consultationOrder.accept.error')), { variant: 'error' })
        }
      }}
      readOnly={!isNetworkMember}
    />
  )
}
